import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { unitCaseEnum } from '../../utils/settings';
import moment from 'moment';

const CheckoutConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  deliveryDetails,
  productDetails,
  invoiceDetails,
  isHomogeneous = false,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { unitCaseWise } = useSelector((state) => ({
    unitCaseWise: state.fmcgReducer.unitCaseWise,
  }));
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMobile ? 'xs' : 'sm'}
      fullWidth
      fullScreen={isMobile}
      aria-labelledby='checkout-confirmation-title'
      PaperProps={{
        sx: {
          m: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '90vh',
          borderRadius: isMobile ? 0 : 2,
        },
      }}
    >
      <DialogTitle
        id='checkout-confirmation-title'
        sx={{
          backgroundColor: '#f4f6f8',
          fontWeight: 'bold',
          textAlign: 'center',
          color: '#637381',
          fontSize: isMobile ? '1.1rem' : '1.25rem',
          py: isMobile ? 1.5 : 2,
          px: isMobile ? 2 : 3,
        }}
      >
        Confirm Allocation Details
      </DialogTitle>

      <DialogContent
        sx={{
          mt: isMobile ? 1 : 2,
          px: isMobile ? 2 : 3,
          pb: isMobile ? 1 : 2,
          overflow: 'auto',
        }}
      >
        {/* Delivery Details */}
        <Box
          sx={{
            mb: isMobile ? 2 : 3,
            p: isMobile ? 1.5 : 2,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            backgroundColor: '#fafafa',
          }}
        >
          <Typography
            variant='subtitle1'
            fontWeight='bold'
            color='#637381'
            sx={{
              mb: 1.5,
              backgroundColor: '#f4f6f8',
              px: 2,
              py: 1,
              mx: isMobile ? -1.5 : -2,
              mt: isMobile ? -1.5 : -2,
              borderRadius: '8px 8px 0 0',
            }}
          >
            Deliver To
          </Typography>
          <Grid
            container
            spacing={isMobile ? 0.5 : 1}
          >
            {deliveryDetails?.company_name && (
              <Grid
                item
                xs={12}
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: isMobile ? '0.875rem' : '0.875rem',
                    lineHeight: isMobile ? 1.4 : 1.5,
                    wordBreak: 'break-word',
                  }}
                >
                  <strong>Firm:</strong> {deliveryDetails.company_name}
                </Typography>
              </Grid>
            )}
            {deliveryDetails?.company_phone_no && (
              <Grid
                item
                xs={12}
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: isMobile ? '0.875rem' : '0.875rem',
                    lineHeight: isMobile ? 1.4 : 1.5,
                  }}
                >
                  <strong>Phone:</strong> {deliveryDetails.company_phone_no}
                </Typography>
              </Grid>
            )}
            {(deliveryDetails?.delivery_Address_line1 ||
              deliveryDetails?.delivery_Address_line2) && (
              <Grid
                item
                xs={12}
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: isMobile ? '0.875rem' : '0.875rem',
                    lineHeight: isMobile ? 1.4 : 1.5,
                    wordBreak: 'break-word',
                  }}
                >
                  <strong>Address:</strong>{' '}
                  {`${deliveryDetails.delivery_Address_line1 || ''} ${
                    deliveryDetails.delivery_Address_line2 || ''
                  }`.trim()}
                </Typography>
              </Grid>
            )}
            {deliveryDetails?.company_gstin && (
              <Grid
                item
                xs={12}
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: isMobile ? '0.875rem' : '0.875rem',
                    lineHeight: isMobile ? 1.4 : 1.5,
                    wordBreak: 'break-all',
                  }}
                >
                  <strong>GSTIN:</strong> {deliveryDetails.company_gstin}
                </Typography>
              </Grid>
            )}
            {deliveryDetails?.pancard_no && (
              <Grid
                item
                xs={12}
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: isMobile ? '0.875rem' : '0.875rem',
                    lineHeight: isMobile ? 1.4 : 1.5,
                    wordBreak: 'break-all',
                  }}
                >
                  <strong>PAN:</strong> {deliveryDetails.pancard_no}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider sx={{ my: isMobile ? 1.5 : 2 }} />

        {/* Product Details */}
        <Box
          sx={{
            mb: isMobile ? 2 : 3,
            p: isMobile ? 1.5 : 2,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            backgroundColor: '#fafafa',
          }}
        >
          <Typography
            variant='subtitle1'
            fontWeight='bold'
            color='#637381'
            sx={{
              mb: 1.5,
              backgroundColor: '#f4f6f8',
              px: 2,
              py: 1,
              mx: isMobile ? -1.5 : -2,
              mt: isMobile ? -1.5 : -2,
              borderRadius: '8px 8px 0 0',
            }}
          >
            Products
          </Typography>
          {isHomogeneous ? (
            <Box
              sx={{
                p: isMobile ? 1.5 : 2,
                border: '2px solid #F49D3F',
                borderRadius: 2,
                backgroundColor: '#FFF8F0',
                textAlign: 'center',
              }}
            >
              <Typography
                variant='body1'
                sx={{
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  lineHeight: isMobile ? 1.4 : 1.5,
                  fontWeight: 'bold',
                  color: '#000',
                  mb: 0.5,
                }}
              >
                Sell Quantity: {productDetails?.sellQty}
              </Typography>
              {productDetails?.availableQty && (
                <Typography
                  variant='caption'
                  sx={{
                    fontSize: isMobile ? '0.75rem' : '0.8rem',
                    color: '#666',
                    fontStyle: 'italic',
                  }}
                >
                  Available: {productDetails.availableQty}
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              {productDetails?.products?.map((product, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: isMobile ? 1 : 1.5,
                    p: isMobile ? 1.5 : 2,
                    border: '2px solid #e8f4f8',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? 1 : 2,
                    backgroundColor: '#fafbfc',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {/* Product Image */}
                  <Box
                    sx={{
                      minWidth: isMobile ? 50 : 60,
                      height: isMobile ? 50 : 60,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid #e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#ffffff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    {product.product_info?.product_logo ? (
                      <img
                        src={product.product_info.product_logo.split(',')[0]}
                        alt={product.product_info?.product_name || 'Product'}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const parent = e.target.parentNode;
                          const fallback =
                            parent.querySelector('.fallback-image');
                          if (fallback) {
                            fallback.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <Box
                      className='fallback-image'
                      sx={{
                        display: product.product_info?.product_logo
                          ? 'none'
                          : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                        color: '#999',
                        textAlign: 'center',
                      }}
                    >
                      No Image
                    </Box>
                  </Box>

                  {/* Product Details */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant='body1'
                      fontWeight='600'
                      sx={{
                        fontSize: isMobile ? '0.85rem' : '.9rem',
                        lineHeight: isMobile ? 1.3 : 1.4,
                        mb: 0.5,
                        color: '#2c3e50',
                      }}
                    >
                      {product.product_info?.product_name || 'Product'}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          fontSize: '0.8rem',
                          color: '#2c3e50',
                          fontWeight: '600',
                        }}
                      >
                        Qty: {product.sellToBuyerQty?.qty}{' '}
                        {unitCaseWise === unitCaseEnum['UNIT']
                          ? product.allocable_quantity?.base_unit || ''
                          : product.allocable_quantity?.unit || ''}
                      </Typography>
                      {product.allocable_quantity?.unit &&
                        product.allocable_quantity?.base_unit && (
                          <Typography
                            variant='caption'
                            sx={{
                              fontSize: isMobile ? '0.7rem' : '0.75rem',
                              color: '#666',
                              fontStyle: 'italic',
                            }}
                          >
                            Available:{' '}
                            {product.allocable_quantity.quantity_in_bags}{' '}
                            {product.allocable_quantity.base_unit} /{' '}
                            {product.allocable_quantity.quantity_in_metric_ton}{' '}
                            {product.allocable_quantity.unit}
                          </Typography>
                        )}
                    </Box>
                  </Box>
                </Box>
              ))}
              <Typography
                variant='body2'
                sx={{
                  mt: 1,
                  fontSize: '1.1rem',
                  lineHeight: isMobile ? 1.4 : 1.5,
                  fontWeight: 'bold',
                }}
              >
                <strong>Total Quantity:</strong>{' '}
                {productDetails?.products?.reduce(
                  (total, product) =>
                    total + (product.sellToBuyerQty?.qty || 0),
                  0,
                )}{' '}
                {unitCaseWise === unitCaseEnum['UNIT']
                  ? productDetails?.products?.[0]?.allocable_quantity
                      ?.base_unit || ''
                  : productDetails?.products?.[0]?.allocable_quantity?.unit ||
                    ''}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: isMobile ? 1.5 : 2 }} />

        {/* Invoice Details */}
        <Box
          sx={{
            p: isMobile ? 1.5 : 2,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            backgroundColor: '#fafafa',
          }}
        >
          <Typography
            variant='subtitle1'
            fontWeight='bold'
            color='#637381'
            sx={{
              mb: 1.5,
              backgroundColor: '#f4f6f8',
              px: 2,
              py: 1,
              mx: isMobile ? -1.5 : -2,
              mt: isMobile ? -1.5 : -2,
              borderRadius: '8px 8px 0 0',
            }}
          >
            Invoice Details
          </Typography>
          <Typography
            variant='body2'
            sx={{
              fontSize: isMobile ? '0.875rem' : '0.875rem',
              lineHeight: isMobile ? 1.4 : 1.5,
              mb: 0.5,
            }}
          >
            <strong>Invoice No:</strong>{' '}
            {invoiceDetails?.invoiceNumber || 'N/A'}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              fontSize: isMobile ? '0.875rem' : '0.875rem',
              lineHeight: isMobile ? 1.4 : 1.5,
            }}
          >
            <strong>Invoice Date:</strong>{' '}
            {invoiceDetails?.invoiceDate
              ? moment(invoiceDetails.invoiceDate).format('DD MMM YYYY')
              : 'N/A'}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: isMobile ? 2 : 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
          backgroundColor: isMobile ? '#f9f9f9' : 'transparent',
        }}
      >
        <Button
          onClick={onClose}
          variant='outlined'
          color='error'
          disabled={loading}
          fullWidth={isMobile}
          sx={{
            minWidth: isMobile ? 'auto' : 120,
            order: isMobile ? 2 : 1,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant='contained'
          color='warning'
          disabled={loading}
          fullWidth={isMobile}
          sx={{
            fontWeight: 'bold',
            minWidth: isMobile ? 'auto' : 120,
            order: isMobile ? 1 : 2,
          }}
        >
          {loading ? 'Processing...' : 'Confirm Checkout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckoutConfirmationModal;
